import { __read } from 'tslib';
import { useCallback, useEffect } from 'react';
import { useCookies } from 'react-cookie';
import { isOnServer } from '../polling/hooks/isOnServer.esm.js';
import { LOCATION_LITE_KEY, parseLocationCookieValue, getCookieWithDefaults, getCookieExpiration } from '@invitae/location-lite-core';
import '../domain/context.esm.js';
import { useLocationLiteDomain } from '../domain/hooks.esm.js';

/**
 * Provides access to getting and setting the LocationLite cookie
 *
 * Consumers typically won't need direct access to this hook - prefer the simpler useLocationLiteCountry
 *
 * Thin wrapper around the context included with `react-cookie`
 *
 * Returns an array with:
 *    cookieContent - the current object value of the cookie. Note that this will always be the true value of the cookie, including unset.
 *                    There is no 'US' fallback built-in to this hook!
 *    setCookieContent - a simple hook to set the cookie with appropriate attributes, automatically appending cookie version.
 *                       if passed a string value, it will be assumed to be an encoded cookie and will set that string directly
 *
 *    Note that implicit typing does not seem to work on this array - please explicitly type the return of this function
 */
var useLocationLiteCookie = function () {
    var _a = __read(useCookies([LOCATION_LITE_KEY]), 3), cookies = _a[0], setCookie = _a[1], removeCookie = _a[2];
    var cookieDomain = useLocationLiteDomain();
    /**
     * Gets/returns the LocationLiteCookie
     */
    var cookieContent = parseLocationCookieValue(cookies[LOCATION_LITE_KEY]);
    /**
     * Deletes cookie on browser
     * As we're not passing `domain` to the `removeCookie`, we will just delete the most specific cookie, like `local.dev.locusdev.net` or on dev `invitae.dev.locusdev.net` (wrong cookies)
     * So, the cookie with right domain (starting with . (dot)) will not be removed
     */
    var deleteCookie = useCallback(function () {
        removeCookie(LOCATION_LITE_KEY, {
            maxAge: -99999999,
        });
    }, [removeCookie]);
    /**
     * Sets the LocationLite cookie value
     */
    var setCookieContent = function (cookieContentToBeSet) {
        deleteCookie();
        var finalCookieContent = typeof cookieContentToBeSet === 'string' ? cookieContentToBeSet : getCookieWithDefaults(cookieContentToBeSet);
        return setCookie(LOCATION_LITE_KEY, finalCookieContent, {
            domain: cookieDomain,
            expires: getCookieExpiration(),
            path: '/',
        });
    };
    useEffect(function () {
        if (isOnServer())
            return;
        deleteCookie();
    }, [deleteCookie]);
    return [cookieContent, setCookieContent];
};

export { useLocationLiteCookie };
//# sourceMappingURL=hooks.esm.js.map
